import React from 'react';
import styled from 'styled-components';
import { Box, SolvTheme, Text } from '@solvhealth/jigsaw-v1';
import Button, { ButtonVariations } from '../../../../../SolvPatternLibrary/Button';
import Observer from '../../../../../SolvPatternLibrary/Profilers/Observer';
import useSelectInsuranceProfile from '../../../../hooks/useSelectInsuranceProfile';
import useModal from '../../../../../../hooks/useModal';
import useSelectFetchInsuranceDetails from '../../../../hooks/useSelectFetchInsuranceDetails';
import { INSURANCE_TUTORIAL_MODAL } from '../../../../../../constants/modalIds';
import { colors } from '../../../../../../constants/colors';
import { biggerThanOrEqualTo, smallerThan } from '../../../../../../core/util/styledComponents';
import { fontFamily, fontSize } from '../../../../../../constants/text';
import { analyticsTrackEvent } from '../../../../../../core/analytics';
import {
  Actions,
  Events,
  Sources,
} from '../../../../../Account/Profile/Insurance/Tutorial/analytics';
import {
  getPlanTypeOrEmptyString,
  isWhitelistedPlanType,
} from '../../../../../Account/Profile/Insurance/util/insuranceDetails';

const CardRoot = styled.div.withConfig({ componentId: 'sc-139px0e-0' })`
  background-color: ${colors.theWhiteAlbum};
  border-radius: 8px;
  border: 1px solid #e6e6e6;
  box-shadow: ${SolvTheme.shadows.sm};
  padding: 28px 24px;
  max-width: 400px;
  margin-top: 16px;
`;

const DesktopPadding = styled.div.withConfig({ componentId: 'sc-139px0e-1' })`
  padding-bottom: 32px;
  ${smallerThan.sm`
    display: none;
  `};
`;

const PinkButton = styled(Button).withConfig({ componentId: 'sc-139px0e-2' })`
  font-size: ${fontSize.default};
  font-family: ${fontFamily.bold};
  width: 100%;
  margin-top: 12px;
`;

const Header = styled.div.withConfig({ componentId: 'sc-139px0e-3' })`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
`;

const Icon = styled.img.withConfig({ componentId: 'sc-139px0e-4' })`
  height: 64px;
  align-items: center;
  margin-right: 12px;
`;

const H2 = styled.h2.withConfig({ componentId: 'sc-139px0e-5' })`
  color: ${colors.squid};
  font-size: ${fontSize.medium};
`;
const P = styled.p.withConfig({ componentId: 'sc-139px0e-6' })`
  color: ${colors.lightText};
  font-size: ${fontSize.mediumSmall};
`;

type Props = {
  userProfileId: string;
};

const Card = (props: Props) => {
  const { userProfileId } = props;
  const insuranceProfile = useSelectInsuranceProfile(userProfileId);
  const insuranceDetails = useSelectFetchInsuranceDetails(userProfileId);
  const planType = getPlanTypeOrEmptyString(insuranceDetails);
  const { openModal } = useModal(INSURANCE_TUTORIAL_MODAL);

  const insuranceProfileId = insuranceProfile && insuranceProfile.id;
  const firstName = insuranceProfile && (insuranceProfile.first_name || insuranceProfile.firstName);
  const insuranceCarrier =
    insuranceProfile && (insuranceProfile.insurer_name || insuranceProfile.insurerName);

  const handleClick = () => {
    analyticsTrackEvent(Events.INSURANCE_TUTORIAL, {
      action: Actions.CLICK_VIEW_TUTORIAL,
      source: Sources.TUTORIAL_CARD,
    });
    openModal();
  };

  const handleViewCard = () =>
    analyticsTrackEvent(Events.INSURANCE_TUTORIAL, {
      action: Actions.SHOW_ENTRY_POINT_CARD,
      userProfileId,
      insuranceProfileId,
    });

  if (!isWhitelistedPlanType(planType)) return null;

  return (
    <Observer data-testid="CardObserver" handleSawComponent={handleViewCard} threshold={0.5}>
      <Box mx={{ _: 2, md: 4 }}>
        <Text fontWeight="bold" style={{ fontSize: '20px' }}>
          Insurance 101
        </Text>
        <CardRoot>
          <Header>
            <Icon src="/images/insurance_tutorial/you_have_insurance.svg" />
            <H2>
              Get to know {firstName}'s {insuranceCarrier} plan
            </H2>
          </Header>
          <P>
            Learn how it works, what's covered, and what all of those bizarre insurance terms mean.
          </P>
          <PinkButton onClick={handleClick} variation={ButtonVariations.pinkPrimary}>
            Let's Go →
          </PinkButton>
        </CardRoot>
        <DesktopPadding />
      </Box>
    </Observer>
  );
};

export default Card;
